/**
 * CR306: Consent section — cookie/consent banner handling
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Shield, Save } from "lucide-react";
import type { Recipe } from "@/lib/recipes-studio/types";
import type { FirecrawlRecipeConfig } from "@/lib/recipes-studio/config-types";
import { DEFAULT_CONSENT_SELECTORS } from "@/lib/recipes-studio/config-types";

interface RecipeConsentSectionProps {
  recipe: Recipe;
  saving: boolean;
  onSave: (updates: Partial<Recipe>) => void;
}

export function RecipeConsentSection({ recipe, saving, onSave }: RecipeConsentSectionProps) {
  const cfg = (recipe.recipe_config ?? {}) as FirecrawlRecipeConfig;
  const consent = cfg.consent ?? {};

  const [enabled, setEnabled] = useState(consent.enabled ?? false);
  const [selectors, setSelectors] = useState(
    (consent.selectors ?? DEFAULT_CONSENT_SELECTORS).join("\n")
  );

  useEffect(() => {
    const c = (recipe.recipe_config ?? {}) as FirecrawlRecipeConfig;
    const con = c.consent ?? {};
    setEnabled(con.enabled ?? false);
    setSelectors((con.selectors ?? DEFAULT_CONSENT_SELECTORS).join("\n"));
  }, [recipe.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentSelectors = (consent.selectors ?? DEFAULT_CONSENT_SELECTORS).join("\n");
  const hasChanges =
    enabled !== (consent.enabled ?? false) ||
    selectors !== currentSelectors;

  const handleSave = useCallback(() => {
    const selectorList = selectors
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const updated: FirecrawlRecipeConfig = {
      ...cfg,
      consent: { enabled, selectors: selectorList },
    };
    onSave({ recipe_config: updated as Record<string, unknown> });
  }, [cfg, enabled, selectors, onSave]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Shield className="h-4 w-4 text-muted-foreground" />
          Consent Handling
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-xs">Auto-accept Cookie Banners</Label>
            <p className="text-[11px] text-muted-foreground">
              Click consent buttons before scraping
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>

        {enabled && (
          <div className="space-y-1.5">
            <Label className="text-xs">CSS Selectors (one per line)</Label>
            <Textarea
              value={selectors}
              onChange={(e) => setSelectors(e.target.value)}
              placeholder="#accept-cookies&#10;.cookie-accept"
              className="font-mono text-xs min-h-[80px]"
            />
          </div>
        )}

        {hasChanges && (
          <Button size="sm" onClick={handleSave} disabled={saving}>
            <Save className="h-3.5 w-3.5 mr-1.5" />
            {saving ? "Saving…" : "Save"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
